import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Hero from "../views/Hero";
import Footer from "../views/Footer";
import HomeBlogsGrid from "../views/HomeBlogsGrid";
import FadeIn from "../components/FadeIn";
import GoogleReviews from "../components/GoogleReviews";
import HorizontalCarousel from "../components/HorizontalCarousel";
import LeadCaptureModal from "../components/LeadCaptureModal";
import { Container, Typography, Card, Button } from "../components/ui";
import { courseCategories } from "../data";
import { getCourseImage } from "../config/courseImages";

const Home = () => {
  const navigate = useNavigate();
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const handleViewAllCourses = () => {
    navigate('/courses?view=all');
  };

  const handleCourseEnquiry = (course) => {
    setSelectedCourse(course);
    setShowLeadModal(true);
  };

  const closeLeadModal = () => {
    setShowLeadModal(false);
    setSelectedCourse(null);
  };

  return (
    <>
      <Hero />
      
      {/* Why Choose BimBytes Section */}
      <Container.Section id="why-choose-us" padding="content" background="gradient">
        <Container size="standard">
          <FadeIn delay={0.2} direction="down" fullWidth>
            <Typography.SectionHeader
              title={
                <>
                  Why Choose <span className="text-gradient">BimBytes Academy</span>?
                </>
              }
              description="We provide industry-driven BIM training that bridges the gap between theory and real-world application, preparing you for high-demand careers in the AEC industry."
            />
          </FadeIn>

          <Container.Grid columns="3" gap="default" className="items-stretch [&>*]:h-full mt-12">
            {[
              { icon: "🏗️", title: "Real-time Live Projects", description: "Training based on actual ongoing international projects for practical experience." },
              { icon: "🛠️", title: "Industry Tools", description: "Master Revit, Navisworks, Dynamo and other essential BIM software." },
              { icon: "💼", title: "Placement Support", description: "Dedicated assistance to help you secure positions in top AEC firms." },
              { icon: "👶", title: "Beginner-Friendly", description: "Courses designed to take you from scratch to an industry professional." },
              { icon: "❓", title: "Complete Doubt Clearance", description: "Personalized attention to ensure all your technical queries are resolved." },
              { icon: "🏢", title: "Industrial-Oriented Training", description: "Curriculum aligned with current industry demands and standards." },
              { icon: "🔄", title: "Flexible Course Module", description: "Customizable learning paths to suit your career goals." },
              { icon: "💻", title: "Online & Offline Mode", description: "Choose between classroom training or interactive live online sessions." },
              { icon: "👨‍🏫", title: "Expert Trainers", description: "Learn from professionals with 10+ years of global industry experience." }
            ].map((feature, index) => (
              <FadeIn key={index} delay={0.2 + index * 0.1} direction="up">
                <Card variant="feature" padding="default" className="h-full flex flex-col items-center text-center">
                  <Card.Icon size="default">
                    <span className="text-3xl">{feature.icon}</span>
                  </Card.Icon>
                  
                  <Card.Title size="default" className="group-hover:text-primary transition-colors duration-300 ease-smooth">
                    {feature.title}
                  </Card.Title>
                  
                  <Card.Content>
                    {feature.description}
                  </Card.Content>
                </Card>
              </FadeIn>
            ))}
          </Container.Grid>
        </Container>
      </Container.Section>

      {/* Google Reviews (API-based, no map) */}
      <GoogleReviews />

      {/* Popular Courses Section */}
      <Container.Section padding="content" background="gradient">
        <Container size="standard">
          <FadeIn delay={0.2} direction="down" fullWidth>
            <Typography.SectionHeader
              title={
                <>
                  Our <span className="text-gradient">Popular Courses</span>
                </>
              }
              description="Featured BIM training programs in Advanced BIM, Automation, Architecture, and Structural disciplines"
            />
          </FadeIn>

          {/* Popular Courses Carousel */}
          <div className="mt-8">
            <HorizontalCarousel 
              ariaLabel="Popular courses" 
              itemWidthPx={320} 
              gapPx={24} 
              autoPlay={false} 
              draggable={true}
            >
              {courseCategories
                .filter(cat => cat.category === 'Popular Courses')
                .flatMap(cat => 
                  cat.courses.map(course => ({
                    title: course.title,
                    description: course.description,
                    category: cat.category
                  }))
                )
                .map((course, index) => (
                  <div 
                    key={index} 
                    className="bg-white rounded-2xl shadow-lg ring-1 ring-border overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer flex flex-col h-full"
                    onClick={() => navigate(`/courses/${course.title.toLowerCase().replace(/\s+/g, "-")}`)}
                  >
                    {/* Course Image */}
                    <img
                      src={getCourseImage(course.title)}
                      alt={course.title}
                      className="w-full h-40 object-cover shrink-0"
                      loading="lazy"
                    />

                    {/* Category Badge */}
                    <div className="p-4 pb-0 shrink-0">
                      <span className="inline-block bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full">
                        {course.category}
                      </span>
                    </div>
                    
                    {/* Course Content */}
                    <div className="p-4 flex flex-col flex-grow">
                      <h3 className="text-lg font-bold text-textPrimary mb-3 group-hover:text-primary transition-colors duration-300 shrink-0">
                        {course.title}
                      </h3>
                      
                      <p className="text-textSecondary text-sm leading-relaxed mb-4 line-clamp-3 flex-grow">
                        {course.description}
                      </p>
                      
                      <div className="flex justify-end gap-2 shrink-0">
                        <Button 
                          variant="ghost" 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/courses/${course.title.toLowerCase().replace(/\s+/g, "-")}`);
                          }}
                        >
                          View Details
                        </Button>
                        <Button 
                          variant="primary" 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCourseEnquiry(course);
                          }}
                        >
                          Enquire Now
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
            </HorizontalCarousel>
          </div>
          
          <FadeIn delay={0.4} direction="up" fullWidth>
            <div className="text-center mt-12">
              <Button 
                variant="primary" 
                size="large"
                onClick={handleViewAllCourses}
              >
                View All Courses
              </Button>
            </div>
          </FadeIn>
        </Container>
      </Container.Section>



      {/* Home Blogs */}
      <Container.Section padding="content" background="gradient">
        <Container size="standard">
          <Container.Grid columns="1" gap="default">
            {/* Blogs Card */}
            <Card padding="default">
              <div className="flex items-center justify-between mb-4">
                <Typography.H5>Latest from our Blog</Typography.H5>
                <a href="/blog" className="text-primary font-semibold hover:underline">View all</a>
              </div>
              <HomeBlogsGrid />
            </Card>
          </Container.Grid>
        </Container>
      </Container.Section>

      {/* Location Map */}
      <Container.Section padding="content" background="white">
        <Container size="standard">
          <div className="mb-4">
            <Typography.H3>Find Us</Typography.H3>
            <Typography.Body color="secondary">Bim Bytes Academy — Chennai & Online</Typography.Body>
          </div>
          <Card padding="none" className="overflow-hidden">
            <iframe
              title="Bim Bytes Academy Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3885.7410347221407!2d80.10356067580467!3d13.115585111703712!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a52633fd7b23c87%3A0x6e207c138841be94!2sBIM%20BYTES%20ACADEMY!5e0!3m2!1sen!2sin!4v1756291115823!5m2!1sen!2sin"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Card>
        </Container>
      </Container.Section>

      <Footer />

      {/* Lead Capture Modal */}
      <LeadCaptureModal 
        isOpen={showLeadModal} 
        onClose={closeLeadModal}
        selectedCourse={selectedCourse}
      />
    </>
  );
};

export default Home;
